import React, { useMemo, useState } from 'react';

import { WHITELISTED_REPORTING_COMPONENT_KEYS } from 'constants/componentKeys/whiteListedReporting.componentKeys';

import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useQueryGetComponentsLibrary } from 'api/queries/getComponentsLibrary.query';

import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ProjectTree } from 'components/common/TreeProgramFilter/ProjectTree/ProjectTree';
import classes from 'components/common/WidgetsLibraryModal/WidgetsLibraryModal.module.scss';
import { Button } from 'components/common/index';

import { IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { Chip } from 'components/DesignSystem/Chip';
import { Modal } from 'components/DesignSystem/Modal';
import { Select } from 'components/DesignSystem/Select';
import { createFilterControl } from 'components/DesignSystem/Select/components/FilterControl/FilterControl';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

const FilterControl = createFilterControl({
    icon: <MuiIcon icon={'filter_alt'} type={'outlined'} fontSize={'18px'} color={'inherit'} />,
    filterName: 'Category',
});

interface Props {
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onSubmit: (componentKey: keyof IHashmap) => void;
    isReportHasWidgets: boolean;
}

export const WidgetsLibraryModal = ({ isModalOpen, setIsModalOpen, onSubmit, isReportHasWidgets }: Props) => {
    const modalTitle = isReportHasWidgets ? 'Add Component' : 'Add the first Component to your new Report';

    const [searchValue, setSearchValue] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>('Most Popular');

    const { sendEventWithDimensions } = useEventWithDimensions();

    const hideModal = (): void => {
        setIsModalOpen(false);
    };

    const handleComponentSelect = (componentKey: keyof IHashmap, componentName: string): void => {
        sendEventWithDimensions({
            category: 'Reporting',
            action: 'Component Selected',
            label: componentName,
        });
        onSubmit(componentKey);
    };

    const { data: widgets, isLoading } = useQueryGetComponentsLibrary();

    const allowedWidgetsForReporting = useMemo(() => {
        return widgets?.filter((widget) => {
            return WHITELISTED_REPORTING_COMPONENT_KEYS.includes(widget.componentKey);
        });
    }, [widgets]);

    const filteredWidgets = useMemo(() => {
        if (!allowedWidgetsForReporting) return [];

        let filtered = allowedWidgetsForReporting;

        if (selectedCategory) {
            filtered = filtered.filter((widget) => widget.categories?.includes(selectedCategory));
        }

        if (searchValue) {
            const lowerSearch = searchValue.toLowerCase();
            filtered = filtered.filter(
                (widget) =>
                    widget.name.toLowerCase().includes(lowerSearch) ||
                    widget.description.toLowerCase().includes(lowerSearch),
            );
        }

        return filtered;
    }, [searchValue, selectedCategory, allowedWidgetsForReporting]);

    const categoryOptions = useMemo(() => {
        if (!widgets) return [];

        const allCategories = widgets.flatMap((widget) => widget.categories || []);
        const distinctCategories = Array.from(new Set(allCategories)).sort((a, b) => a.localeCompare(b));

        return distinctCategories.map((category) => ({
            label: category,
            value: category,
        }));
    }, [widgets]);

    if (isLoading) {
        return <OverlayWithSpinner />;
    }

    const searchInputPlaceholder = selectedCategory
        ? `Search components in ${selectedCategory}`
        : 'Search components....';

    return (
        <Modal open={isModalOpen} onClose={hideModal} title={modalTitle}>
            <div className={classes.widgetsLibraryModalContainer}>
                <ProjectTree preventNavigation={true} />

                <div className={classes.filtersContainer}>
                    <TextInput
                        value={searchValue}
                        onClear={() => setSearchValue('')}
                        onChange={(e) => setSearchValue(e.target.value)}
                        id={'widgets-library-search'}
                        placeholder={searchInputPlaceholder}
                        endIcon={<Icon name={'search'} size={'1.6rem'} color={'var(--text-placeholder)'} />}
                    />
                    <Select
                        value={
                            selectedCategory
                                ? categoryOptions.find((opt) => opt.value === selectedCategory) || null
                                : null
                        }
                        options={categoryOptions}
                        onChange={(option) => setSelectedCategory(option ? option.value : null)}
                        placeholder={'All'}
                        boldPlaceholder={true}
                        isClearable
                        components={{
                            Control: FilterControl,
                        }}
                    />
                </div>

                {filteredWidgets.length === 0 && (
                    <div className={classes.noDataFound}>
                        <MuiIcon
                            icon={'search_off_outlined'}
                            fontSize={'9rem'}
                            color={'var(--text-quaternary)'}
                            type={'outlined'}
                        />
                        <div className={classes.noDataFoundTitle}>No results found</div>
                        <div className={classes.noDataFoundSubTitle}>
                            We couldn't find any components matching your search. Try adjusting your filters or search
                            terms.
                        </div>
                    </div>
                )}
                <div className={`${classes.widgetsWrapper} ${filteredWidgets.length === 0 && classes.empty}`}>
                    {filteredWidgets.map((widget, index) => (
                        <div
                            className={classes.widgetCard}
                            key={widget.componentKey + index.toString()}
                            onClick={() => handleComponentSelect(widget.componentKey, widget.name)}
                        >
                            <div className={classes.title}>{widget.name}</div>

                            <div className={classes.imageWrapper}>
                                {widget.picture ? (
                                    <img
                                        className={classes.image}
                                        src={`data:image/svg+xml;base64,${widget.picture.trim()}`}
                                        alt={widget.name || 'widget image'}
                                    />
                                ) : (
                                    <div className={classes.fallbackImage}>
                                        <MuiIcon icon={'auto_graph_sharp'} fontSize={'6rem'} />
                                    </div>
                                )}
                            </div>
                            <div className={classes.tagsWrapper}>
                                {widget.categories?.map((category, index) => (
                                    <Chip key={index + category} label={category} size={'default'} type={'default'} />
                                ))}
                            </div>
                            <div className={classes.details}>{widget.description || ''}</div>

                            <Button
                                size={'large'}
                                variant={'primary'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleComponentSelect(widget.componentKey, widget.name);
                                }}
                                startIcon={<MuiIcon icon={'add'} color={'var(--button-primary-fg)'} />}
                            >
                                Add {widget.name}
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};
